/**
 * ENG-4568: Process restart flags written by channel MCP subprocesses
 * (telegram-channel etc) when an authorised user posts `/restart`. Each
 * cycle, the manager poll loop calls processRestartFlags() to:
 *
 *   1. Read flag files in ~/.augmented/restart-flags/
 *   2. For each flag, verify the agent is one we manage and is on the
 *      Claude Code framework (out of scope for v1: openclaw, nemoclaw)
 *   3. Stop the agent's tmux session via stopPersistentSession() — the
 *      ensure-session path on the same poll respawns it
 *   4. Post an ack back to the originating channel
 *   5. Delete the flag so we don't re-process it
 *
 * The handler is intentionally framework-aware but channel-agnostic in its
 * core: each `source` gets a small dispatcher for the post-restart ack.
 * Today only Telegram is wired up.
 */

import { deleteRestartFlag, readRestartFlags, type RestartFlag } from './restart-flags.js';

export interface ProcessRestartFlagsOpts {
  /** Logger function from manager-worker */
  log: (msg: string) => void;
  /**
   * Returns the framework code for an agent's code_name, or null if the
   * agent is unknown to this manager. Used to gate /restart to claude-code
   * only per the ENG-4568 v1 scope.
   */
  resolveFramework: (codeName: string) => string | null;
  /**
   * Stop helper from persistent-session. Kills the tmux session and clears
   * internal state so the next ensure-session pass respawns. Only invoked
   * for claude-code agents.
   *
   * ENG-6229: receives the full flag so the manager can derive the breaker
   * reason from `flag.source` — `'agent'` records as `'agent-requested'`,
   * everything else stays `'channel-restart-flag'`.
   */
  stopSession: (codeName: string, flag: RestartFlag) => void;
  /**
   * Returns the Telegram bot token + chat allowlist for an agent, or null
   * if the agent has no telegram channel binding. The handler uses this to
   * post the post-restart confirmation back to the originating chat.
   */
  getTelegramTokens: (codeName: string) => { token: string; allowedChats?: string[] } | null;
  /**
   * Send a Telegram message via the manager's existing `telegramApiCall`
   * helper. Kept as an injected callback so this module doesn't have to
   * reach into manager-worker's https client.
   */
  sendTelegram: (
    botToken: string,
    method: 'sendMessage',
    body: Record<string, unknown>,
  ) => Promise<{ ok: boolean; description?: string }>;
  /**
   * Returns the Slack bot token for an agent, or null if no slack binding
   * exists.
   */
  getSlackToken: (codeName: string) => string | null;
  /**
   * Post a Slack message (chat.postMessage). Returns ok flag and an
   * optional error string mirroring the Slack Web API shape.
   */
  sendSlack: (
    botToken: string,
    body: Record<string, unknown>,
  ) => Promise<{ ok: boolean; error?: string }>;
  /**
   * ENG-6399: report this restart to the API so it lands in audit_log with a
   * reason (POST /host/restart-event). Injected as a callback — like
   * sendTelegram — so this module doesn't reach into manager-worker's HTTP
   * client. The manager maps `flag.source`/`flag.reason` onto the canonical
   * audit source and resolves the agent_id. Best-effort: a reporting failure
   * must never block the restart, so the handler calls it fire-and-forget.
   */
  reportRestartEvent?: (flag: RestartFlag) => Promise<void>;
}

export async function processRestartFlags(opts: ProcessRestartFlagsOpts): Promise<void> {
  const flags = readRestartFlags();
  if (flags.length === 0) return;

  for (const flag of flags) {
    try {
      await processOne(flag, opts);
    } catch (err) {
      opts.log(`[restart-handler] Failed to process flag for '${flag.codeName}': ${(err as Error).message}`);
      // Surface the failure to the operator instead of leaving them with
      // just the "⏳ queued" ack and silence (CodeRabbit feedback): the
      // resolveFramework / stopSession call paths can throw, and silent
      // log-only handling hides the real failure mode.
      try {
        await sendError(flag, opts, `❌ Restart failed for \`${flag.codeName}\`: internal error. Check manager logs.`);
      } catch (ackErr) {
        opts.log(`[restart-handler] Error-ack send failed for '${flag.codeName}': ${(ackErr as Error).message}`);
      }
    } finally {
      // Always delete — a stuck flag would otherwise loop the manager into
      // restarting the same agent every cycle. If processing legitimately
      // failed, the user will see the error reply (or absence of an ack)
      // and can re-issue /restart.
      try {
        deleteRestartFlag(flag.codeName);
      } catch (err) {
        opts.log(`[restart-handler] Failed to delete flag for '${flag.codeName}': ${(err as Error).message}`);
      }
    }
  }
}

async function processOne(flag: RestartFlag, opts: ProcessRestartFlagsOpts): Promise<void> {
  const framework = opts.resolveFramework(flag.codeName);
  if (framework === null) {
    opts.log(`[restart-handler] Ignoring /restart for unknown agent '${flag.codeName}'`);
    await sendError(flag, opts, `Agent \`${flag.codeName}\` is not managed by this host.`);
    return;
  }

  if (framework !== 'claude-code') {
    opts.log(`[restart-handler] Ignoring /restart for '${flag.codeName}' — framework is '${framework}', not claude-code`);
    await sendError(flag, opts, `\`/restart\` is only supported for Claude Code agents (this agent runs on \`${framework}\`).`);
    return;
  }

  // ENG-6229: the self-restart path (source 'agent') carries the agent's
  // own reason for the log; surface it so an operator scanning manager.log
  // can see why the agent asked to reload.
  const reasonSuffix = flag.source === 'agent' && flag.reason ? ` reason="${flag.reason}"` : '';
  opts.log(`[restart-handler] Restarting tmux session for '${flag.codeName}' (source: ${flag.source})${reasonSuffix}`);
  opts.stopSession(flag.codeName, flag);

  // ENG-6399: report the restart to audit_log (best-effort, fire-and-forget).
  // The reason otherwise lives only here in manager.log; this is the channel
  // that gets it into the immutable, queryable audit trail.
  if (opts.reportRestartEvent) {
    void opts.reportRestartEvent(flag).catch((err) => {
      opts.log(`[restart-handler] restart-event report failed for '${flag.codeName}': ${(err as Error).message}`);
    });
  }

  // Downgraded from "✅ Restarted" — at this point we've only killed the
  // session. The respawn happens on the same poll's ensure-session pass and
  // persistent-session.ts doesn't expose a "session healthy" signal we can
  // wait on without restructuring that module. Saying "Restarted" before
  // the new session is up is a false positive (CodeRabbit feedback).
  //
  // ENG-6229: source 'agent' has no channel reply routing (the agent already
  // told the human via its ask_user confirmation before calling
  // request_restart), so sendAck logs+skips it via the unknown-source branch —
  // no channel spam, by design.
  await sendAck(flag, opts, `🔄 Restart initiated for \`${flag.codeName}\` — the replacement session is being created.`);
}

async function sendAck(flag: RestartFlag, opts: ProcessRestartFlagsOpts, text: string): Promise<void> {
  if (flag.source === 'telegram') {
    const tokens = opts.getTelegramTokens(flag.codeName);
    if (!tokens) {
      opts.log(`[restart-handler] No telegram tokens cached for '${flag.codeName}' — skipping ack`);
      return;
    }
    const chatId = flag.reply?.['chat_id'];
    if (chatId == null) return;
    const messageId = flag.reply?.['message_id'];
    const body: Record<string, unknown> = { chat_id: chatId, text, parse_mode: 'Markdown' };
    if (messageId != null) body.reply_to_message_id = Number(messageId);
    try {
      const resp = await opts.sendTelegram(tokens.token, 'sendMessage', body);
      if (!resp.ok) {
        opts.log(`[restart-handler] Telegram ack failed for '${flag.codeName}': ${resp.description ?? 'unknown'}`);
      }
    } catch (err) {
      opts.log(`[restart-handler] Telegram ack threw for '${flag.codeName}': ${(err as Error).message}`);
    }
    return;
  }

  if (flag.source === 'slack') {
    const token = opts.getSlackToken(flag.codeName);
    if (!token) {
      opts.log(`[restart-handler] No slack token cached for '${flag.codeName}' — skipping ack`);
      return;
    }
    const channel = flag.reply?.['channel'];
    if (channel == null) return;
    const threadTs = flag.reply?.['thread_ts'];
    const body: Record<string, unknown> = { channel, text };
    // Reply in-thread when we have one so the ack stays scoped to the
    // restart command's conversation rather than spamming the channel.
    if (threadTs != null) body.thread_ts = String(threadTs);
    try {
      const resp = await opts.sendSlack(token, body);
      if (!resp.ok) {
        opts.log(`[restart-handler] Slack ack failed for '${flag.codeName}': ${resp.error ?? 'unknown'}`);
      }
    } catch (err) {
      opts.log(`[restart-handler] Slack ack threw for '${flag.codeName}': ${(err as Error).message}`);
    }
    return;
  }

  opts.log(`[restart-handler] Unknown ack source '${flag.source}' for '${flag.codeName}' — skipping`);
}

async function sendError(flag: RestartFlag, opts: ProcessRestartFlagsOpts, text: string): Promise<void> {
  // Same shape as sendAck — separate function for clarity at the call site.
  await sendAck(flag, opts, text);
}
